import { Box, Typography, Theme, Avatar } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CommonButton from '../../../../components/buttons/CommonButton';
import FormTextField from '../../../../components/formFields/FormTextField';
import ReviewPopup from '../../../../components/ReviewPopup';
import { useState } from 'react';
import ApprovalPopup from '../../../../components/ApprovalPopup';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import { ReactComponent as PersonIcon } from '../../../../components/person.svg';
import { ReactComponent as PersonDeactivatedIcon } from '../../../../components/person-deactivated.svg';
import { useForm, FormProvider } from 'react-hook-form';
import { StatusChip } from '../../../../components/StatusChip';
import { ApprovalStatusMap } from '../constants';
import { ApprovalFlowDetails, ApprovalTimelineEvent, ErrorData } from '../../../../types';
import moment from 'moment';
import { ApproveSettlement, postComment } from '../../../../api/paymentsApi/settlementsApi';
import useSnackBar from '../../../../hooks/useSnackBar';
import { useHasPermission } from '../../../../hooks/useHasPermission';
import { AxiosError } from 'axios';
import theme from '../../../../theme';
import Popup from '../../../../components/Popup';
import InactiveUserName, {
    DEACTIVATED_HOVER_AREA_SX,
    DeactivatedHoverLabel,
} from '../../../../components/InactiveUserName';
import { POPUP_DESCRIPTIONS } from '../../../../constants/popupDescriptions';

const useStyles = makeStyles()((theme: Theme) => ({
    formLeftReviewContainer: {
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.primary.light,
        borderRadius: '16px',
        padding: '20px',
        minHeight: '250px',

        fontFamily: 'Poppins',
        color: '#ffffff',

        '@media (max-width: 1200px)': {
            maxWidth: '500px'
        },

        '@media (max-width: 650px)': {
            padding: '16px',
        },
    },

    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
    },

    title: {
        fontFamily: 'Poppins',
        fontWeight: 600,
        fontSize: '24px',
        color: '#FFFFFF',
    },

    statusChip: {
        fontFamily: 'Poppins',
        backgroundColor: 'rgba(255,255,255,0.08)',
        color: 'rgba(255,255,255,0.65)',
        fontWeight: 600,
        borderRadius: '14px',
        height: '32px',
    },

    submitButtonWrapper: {
        marginTop: '12px',
        marginBottom: '20px',
        width: '100%',
    },

    commentFieldWrapper: {
        marginBottom: '12px',
    },

    commentsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        maxHeight: '194px',
        overflowY: 'auto',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '6px',
        padding: '12px',
        '&::-webkit-scrollbar': {
            width: '5px',
            height: '5px',
            borderRadius: '20px',
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: 20,
            backgroundColor: theme.palette.secondary.dark,
        },
    },

    commentItem: {
        display: 'flex',
        gap: '14px',
        padding: '8px 0px 8px 0px',

        '&:last-child': {
            borderBottom: 'none',
            paddingBottom: 0,
        },
    },

    avatar: {
        width: '35px',
        height: '35px',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: theme.palette.info.main,
        color: '#FFFFFF',
    },

    commentContent: {
        flex: 1,
    },

    nameRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        marginBottom: '2px',
    },

    commenterName: {
        fontFamily: 'Poppins',
        fontWeight: 600,
        fontSize: '14px',
        color: '#FFFFFF',
        lineHeight: 1.2,
    },

    commenterRole: {
        fontFamily: 'Poppins',
        fontSize: '12px',
        color: 'rgba(255,255,255,0.65)',
        lineHeight: 1.2,
    },

    commentDate: {
        fontFamily: 'Poppins',
        fontSize: '12px',
        color: 'rgba(255,255,255,0.65)',
        // marginBottom: '8px',
    },

    commentText: {
        fontFamily: 'Poppins',
        fontSize: '14px',
        color: '#FFFFFF',
        lineHeight: 1.5,
        whiteSpace: 'pre-line'
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },

    heading: {
        fontFamily: 'Poppins',
        fontSize: '20px',
        fontWeight: 600,
        color: '#FFFFFF',
    },

    approvalStatus: {
        fontFamily: 'Poppins',
        fontSize: '12px',
        fontWeight: 500,
        color: 'rgba(255,255,255,0.6)',
        background: 'rgba(255,255,255,0.08)',
        padding: '4px 10px',
        borderRadius: '12px',
    },
    postCommentWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '12px',
        marginBottom: '12px',
    },
    changesRequestedWrapper: {
        marginTop: '16px',
        marginBottom: '20px',
    },

    changesRequestedHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '4px',
    },

    changesRequestedIcon: {
        color: theme.palette.error.main,
        fontSize: '18px',
    },

    approvedIcon: {
        color: theme.palette.success.main,
        fontSize: '18px',
    },

    changesRequestedTitle: {
        fontSize: '12px',
        fontFamily: 'Poppins',
        fontWeight: 500,
        color: theme.palette.error.main,
    },

    approvedTitle: {
        fontFamily: 'Poppins',
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',
        color: theme.palette.success.main,
    },

    changesRequestedTime: {
        fontFamily: 'Poppins',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '18px',
        color: '#9FB0C7',
        marginLeft: '26px',
        marginBottom: '12px',
    },

    changesRequestedCard: {
        marginLeft: '26px',
        border: '1px solid rgba(255, 107, 107, 0.35)',
        background: 'rgba(255, 107, 107, 0.08)',
        borderRadius: '6px',
        padding: '16px 18px',
    },

    approvedCard: {
        marginLeft: '26px',
        border: '1px solid rgba(16, 185, 129, 0.35)',
        background: 'rgba(16, 185, 129, 0.08)',
        borderRadius: '6px',
        padding: '16px 18px',
    },

    changesRequestedText: {
        fontFamily: 'Poppins',
        fontStyle: 'italic',
        fontWeight: 400,
        fontSize: '15px',
        lineHeight: '24px',
        color: '#FFFFFF',
    },
    statusBadge: {
        justifySelf: 'end',

    },

    requiredApprovalsContainer: {
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '10px',
        padding: '12px 14px',
        marginBottom: '16px',
    },

    requiredApprovalsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
    },

    requiredApprovalsLabel: {
        fontSize: '13px',
        fontFamily: 'Poppins',
        fontWeight: 600,
        color: '#FFFFFF',
    },

    requiredApprovalsCount: {
        fontSize: '12px',
        fontWeight: 500,
        color: 'rgba(255,255,255,0.55)',
    },

    approvalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingTop: '8px',
        // borderTop: '1px solid rgba(255,255,255,0.07)',
    },

    approvalRowLeft: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },

    approvalRowName: {
        fontSize: '13px',
        fontFamily: 'Poppins',
        fontWeight: 600,
        color: '#FFFFFF',
    },

    approvalRowRole: {
        fontSize: '11px',
        fontFamily: 'Poppins',
        color: 'rgba(255,255,255,0.5)',
    },

    approvalRowRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '2px',
    },

    approvalStatusPending: {
        fontSize: '12px',
        fontWeight: 500,
        color: theme.palette.secondary.main,
    },

    approvalStatusApproved: {
        fontSize: '12px',
        fontWeight: 500,
        color: theme.palette.success.main,
    },

    approvalRowTimestamp: {
        fontSize: '11px',
        fontFamily: 'Poppins',
        color: theme.palette.secondary.main,
    },
}));

interface ReviewApprovalProps {
    settlementId: number;
    refreshSettlement: () => void;
    settlementDetails: { settlementAmount: number; caseName: string };
    settlement?: ApprovalFlowDetails;
    isSaveEnabled?: boolean;
}

interface RequiredApprover {
    id: number;
    name: string;
    isDeactivated?: boolean;
    role?: string;
    status: 'Approved' | 'Pending' | 'ChangesRequested';
    timestamp?: string;
}

const ReviewApprovalSection = ({ settlementId, refreshSettlement, settlementDetails, settlement, isSaveEnabled }: ReviewApprovalProps) => {
    const styles = useStyles();
    const { setAlert } = useSnackBar();

    const canRequestApproval = useHasPermission('Payments', 'Request Settlement Payment Approval');
    const canApprovePayment = useHasPermission('Payments', 'Approve Settlement Payment');
    const userRole = localStorage.getItem('userRole');
    const canComment = useHasPermission('Payments', 'View');
    const [isConfirmationPopupOpen, setIsConfirmationPopupOpen] = useState(false);
    const [isApprovalConfirmationPopupOpen, setIsApprovalConfirmationPopupOpen] = useState(false);
    const [isChangesRequestedPopupOpen, setIsChangesRequestedPopupOpen] = useState(false);

    const getInitials = (name: string): string => {
        if (!name) return '';
        return name
            .split(' ')
            .map(part => part.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };
    const [isReviewPopupOpen, setIsReviewPopupOpen] = useState(false);
    const [isApprovalPopupOpen, setIsApprovalPopupOpen] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [isApproval, setIsApproval] = useState(false);
    const approvalStatus = settlement?.payGateStatus || '';

    const requiredApprovers: RequiredApprover[] = [
        ...(settlement?.approvals || []).filter((a: { approver: string; roleCode: string; time: string; approvalComment?: string; isRequired: boolean }) => a.isRequired).map((a: { approver: string; approverIsDeactivated?: boolean; roleCode: string; time: string; approvalComment?: string; isRequired: boolean }, index: number) => ({
            id: index,
            name: a.approver,
            isDeactivated: !!a.approverIsDeactivated,
            role: a.roleCode,
            status: 'Approved' as const,
            timestamp: moment(a.time).format('MMM DD, YYYY · h:mm A'),
        })),
        ...(settlement?.pendingApprovals || []).filter((a: { roleCode: string; isRequired: boolean }) => a.isRequired).map((a: { roleCode: string; isRequired: boolean }, index: number) => ({
            id: (settlement?.approvals?.length || 0) + index,
            name: a.roleCode,
            status: 'Pending' as const,
        })),
        ...(settlement?.requestChanges || []).filter((a: { requester: string; roleCode: string; time: string; changeComment?: string; isRequired: boolean }) => a.isRequired).map((a: { requester: string; requesterIsDeactivated?: boolean; roleCode: string; time: string; changeComment?: string; isRequired: boolean }, index: number) => ({
            id: (settlement?.approvals?.length || 0) + (settlement?.pendingApprovals?.length || 0) + index,
            name: a.requester,
            isDeactivated: !!a.requesterIsDeactivated,
            role: a.roleCode,
            status: 'ChangesRequested' as const,
            timestamp: moment(a.time).format('MMM DD, YYYY · h:mm A'),
        })),
    ];



    const formMethods = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            description: '',
        }
    });

    const {
        reset,
        watch,
    } = formMethods;

    // const { watch } = useFormContext();
    const commentValue = watch('description');

    const handleReviewSuccess = () => {
        refreshSettlement();
        setIsReviewPopupOpen(false);
    }

    const handleCloseReviewPopup = () => {
        setIsReviewPopupOpen(false);
    }
    const handleCloseApprovalPopup = () => {
        setIsApprovalPopupOpen(false);
    }

    const handleApprove = () => {
        if (!isSaveEnabled) {
            setIsApproval(true);
            setIsApprovalPopupOpen(true);
        } else {
            setIsApproval(false);
            setIsApprovalConfirmationPopupOpen(true);
        }
    }


    const handleRequestChanges = () => {
        if (!isSaveEnabled) {
            setIsApproval(false);
            setIsApprovalPopupOpen(true);
        } else {
            setIsApproval(false);
            setIsChangesRequestedPopupOpen(true);
        }
    }

    const handleApprovalSubmit = (comment: string, status: string) => {
        setIsFormSubmitting(true);
        setIsApprovalPopupOpen(false);
        ApproveSettlement(settlementId, status, comment)
            .then(() => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: status === 'APPROVE' ? 'Settlement approved successfully' : 'Changes requested successfully',
                    type: 'success',
                }));
                refreshSettlement();
            })
            .catch((error: AxiosError<ErrorData>) => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: error?.response?.data?.message || (status === 'APPROVE' ? 'Failed to approve settlement' : 'Failed to request changes'),
                    type: 'error',
                }));
            });
    }

    const handleSubmitForReview = () => {
        if (!isSaveEnabled) {
            setIsReviewPopupOpen(true);
        } else {
            setIsConfirmationPopupOpen(true);
        }
    }

    const handlePostComment = () => {
        if (!commentValue || commentValue.trim() === '') return;
        setIsFormSubmitting(true);
        postComment(settlementId, commentValue)
            .then(() => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: 'Comment posted successfully',
                    type: 'success',
                }));
                reset({ description: '' });
                refreshSettlement();
            })
            .catch((error: AxiosError<ErrorData>) => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: error?.response?.data?.message || 'Failed to post comment',
                    type: 'error',
                }));
            });
    }

    return (
        <Box className={styles.classes.formLeftReviewContainer} mt={2}>
            <Box className={styles.classes.headerContainer}>
                <Typography className={styles.classes.heading}>
                    Review & Approval
                </Typography>

                <Box className={styles.classes.statusBadge}                      >
                    <Box sx={{ '& .MuiChip-root': { height: '25px !important' }, '& .MuiChip-label': { fontSize: '12px !important', fontFamily: 'Poppins !important' } }}>
                        <StatusChip
                            label={ApprovalStatusMap[approvalStatus as keyof typeof ApprovalStatusMap]?.label || approvalStatus}
                            color={ApprovalStatusMap[approvalStatus as keyof typeof ApprovalStatusMap]?.color || 'default'}
                        />
                    </Box>
                </Box>


            </Box>

            {requiredApprovers.length > 0 && <Box className={styles.classes.requiredApprovalsContainer}>
                <Box className={styles.classes.requiredApprovalsHeader}>
                    <Typography className={styles.classes.requiredApprovalsLabel}>Required Approvals</Typography>
                    <Typography className={styles.classes.requiredApprovalsCount}>
                        {requiredApprovers.filter(a => a.status === 'Approved').length}/{requiredApprovers.length} approved
                    </Typography>
                </Box>

                {requiredApprovers.map((approver, index) => (
                    <Box key={approver.id} className={styles.classes.approvalRow} mt={index === 0 ? 0 : 1}>
                        <Box className={styles.classes.approvalRowLeft} sx={DEACTIVATED_HOVER_AREA_SX}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {approver.isDeactivated ? <PersonDeactivatedIcon /> : <PersonIcon />}
                                <InactiveUserName isInactive={approver.isDeactivated} withTooltip={false}>
                                    <Typography className={styles.classes.approvalRowName}>{approver.name}</Typography>
                                </InactiveUserName>
                            </Box>
                            {(approver.role || approver.isDeactivated) && (
                                <Typography className={styles.classes.approvalRowRole}>
                                    {approver.role}
                                    {approver.isDeactivated && <DeactivatedHoverLabel ml={approver.role ? 1 : 0} />}
                                </Typography>
                            )}
                        </Box>
                        <Box className={styles.classes.approvalRowRight}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {approver.status === 'Approved'
                                    ? <TaskAltIcon sx={{ fontSize: '16px', color: theme.palette.success.main }} />
                                    : approver.status === 'ChangesRequested'
                                        ? <WarningAmberOutlinedIcon sx={{ fontSize: '16px', color: theme.palette.error.main }} />
                                        : <HistoryToggleOffIcon sx={{ fontSize: '16px', color: theme.palette.secondary.main }} />
                                }
                            <Typography
                                className={
                                    approver.status === 'Approved'
                                        ? styles.classes.approvalStatusApproved
                                        : approver.status === 'ChangesRequested'
                                            ? styles.classes.changesRequestedTitle
                                            : styles.classes.approvalStatusPending
                                }
                            >
                                {approver.status === 'ChangesRequested' ? 'Changes Requested' : approver.status}
                            </Typography>
                            </Box>
                            {approver.timestamp && (
                                <Typography className={styles.classes.approvalRowTimestamp}>{approver.timestamp}</Typography>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
            }

            <Box mb={2}>
                {approvalStatus === 'PENDING_REVIEW' && canApprovePayment && (settlement?.pendingApprovals || []).some((a: { roleCode: string }) => a.roleCode?.toLowerCase() === userRole?.toLowerCase()) ? (
                    <Box display="flex" gap={'30px'}>
                        <Box flex={1} sx={{ '& .MuiButton-root': { minHeight: '36px' }, '& .MuiTypography-root': { fontSize: '14px' } }}>
                            <CommonButton
                                disabled={isFormSubmitting}
                                type="success"
                                buttonText="Approve"
                                onButtonClick={handleApprove}
                            />
                        </Box>
                        <Box flex={1} sx={{ '& .MuiButton-root': { minHeight: '36px' }, '& .MuiTypography-root': { fontSize: '14px' } }}>
                            <CommonButton
                                disabled={isFormSubmitting}
                                type="error"
                                buttonText="Request Changes"
                                onButtonClick={handleRequestChanges}
                            />
                        </Box>
                    </Box>
                ) : (((approvalStatus === 'WAITING_FOR_SUBMISSION' || approvalStatus === 'CHANGES_REQUESTED') && canRequestApproval) && (
                    <CommonButton
                        disabled={isFormSubmitting}
                        type="infoMain"
                        buttonText="Submit For Review"
                        onButtonClick={handleSubmitForReview}
                    />
                ))}

            </Box>


            {canComment &&
                <FormProvider {...formMethods}>
                    <Box sx={{ '& .MuiInputBase-inputMultiline': { maxHeight: '50px !important' } }}>
                        <FormTextField
                            isMultiline
                            isDescription
                            name='description'
                            label='Add a comment...'
                        />
                    </Box>

                    <Box className={styles.classes.postCommentWrapper}>
                        {<Box sx={{ '& .MuiButton-root': { minHeight: '35px' }, '& .MuiTypography-root': { fontSize: '12px', fontFamily: 'Poppins' } }}>
                            <CommonButton
                                disabled={!watch('description') || isFormSubmitting}
                                buttonText="Post Comment"
                                type="infoMain"
                                onButtonClick={handlePostComment}
                            />
                        </Box>}
                    </Box>
                </FormProvider>
            }
            {settlement?.approvalTimeline && (() => {
                const commentEventTypes = ['REQUESTED_CHANGES', 'RESET_DUE_TO_EDIT', 'APPROVED', 'USER_COMMENT','SUBMITTED_FOR_REVIEW'];
                const allComments = (settlement?.approvalTimeline as ApprovalTimelineEvent[])
                    .filter((comment: ApprovalTimelineEvent) => commentEventTypes.includes(comment.eventSubType))
                    .slice()
                    .reverse();
                // const visibleComments = showAllComments ? allComments : allComments.slice(0, 2);
                return (
                    <>
                        {allComments && allComments.length > 0 &&
                            <Box className={styles.classes.commentsList}>
                                {allComments.map((comment: ApprovalTimelineEvent) => (
                                    <Box key={comment.id} className={styles.classes.commentItem}>
                                        <Avatar className={styles.classes.avatar}>
                                            {getInitials(comment.actedByName)}
                                        </Avatar>

                                        <Box className={styles.classes.commentContent}>
                                            <Box className={styles.classes.nameRow} sx={DEACTIVATED_HOVER_AREA_SX}>
                                                <InactiveUserName isInactive={comment.actedByIsDeactivated} withTooltip={false}>
                                                    <Typography className={styles.classes.commenterName}>
                                                    {comment.actedByName}
                                                </Typography>
                                                </InactiveUserName>
                                                <Typography className={styles.classes.commenterRole}>
                                                    {comment.actedByRole}
                                                </Typography>
                                                {comment.actedByIsDeactivated && <DeactivatedHoverLabel ml={0} />}
                                            </Box>
                                            <Box className={styles.classes.nameRow}>
                                                <Typography className={styles.classes.commentDate}>
                                                    {moment(comment.createdAt).format('MMM DD, YYYY · h:mm A')}
                                                </Typography>
                                                {(comment.eventSubType === 'APPROVED' || comment.eventSubType === 'REQUESTED_CHANGES') ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 4px', '& .MuiChip-root': { height: '16px' }, '& .MuiChip-label': { fontSize: '12px !important', padding: '0px 6px !important' } }}>
                                                        <StatusChip
                                                            label={ApprovalStatusMap[comment.eventSubType as keyof typeof ApprovalStatusMap]?.label || comment.eventSubType}
                                                            color={ApprovalStatusMap[comment.eventSubType as keyof typeof ApprovalStatusMap]?.color || 'default'}
                                                        />
                                                    </Box>
                                                ) : comment.eventSubType === 'RESET_DUE_TO_EDIT' ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 4px', '& .MuiChip-root': { height: '16px' }, '& .MuiChip-label': { fontSize: '10px !important', padding: '0px 6px !important' } }}>
                                                        <StatusChip
                                                            label={'Auto'}
                                                            color={'default'}
                                                        />
                                                    </Box>) : comment.eventSubType === 'SUBMITTED_FOR_REVIEW' ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 4px', '& .MuiChip-root': { height: '16px' }, '& .MuiChip-label': { fontSize: '10px !important', padding: '0px 6px !important' } }}>
                                                            <StatusChip
                                                                label={'SUBMITTED_FOR_REVIEW'}
                                                                color={'#0da8c9'}
                                                            />
                                                        </Box>) : null}

                                            </Box>

                                            <Typography className={styles.classes.commentText}>
                                                {comment.comment}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>}
                        {/* {allComments.length > 2 && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                                <Typography
                                    onClick={() => setShowAllComments(prev => !prev)}
                                    sx={{ fontFamily: 'Poppins', fontSize: '12px', fontWeight: 500, color: 'rgba(255,255,255,0.55)', cursor: 'pointer', '&:hover': { color: '#ffffff' } }}
                                >
                                    {showAllComments ? 'See Less' : `See ${allComments.length - 2} More`}
                                </Typography>
                            </Box>
                        )} */}
                    </>
                );
            })()}


            <ReviewPopup isOpen={isReviewPopupOpen} onClose={handleCloseReviewPopup} settlementId={settlementId} settlementDetails={settlementDetails} onSuccess={handleReviewSuccess} />
            <ApprovalPopup isOpen={isApprovalPopupOpen} onClose={handleCloseApprovalPopup} isApproval={isApproval} onSubmit={handleApprovalSubmit} />

            <Popup
                isOpen={isConfirmationPopupOpen}
                headlineText={POPUP_DESCRIPTIONS.submitForReview.headlineText}
                contentText={POPUP_DESCRIPTIONS.submitForReview.contentText}
                onMainButtonClick={() => {
                    setIsReviewPopupOpen(true)
                    setIsConfirmationPopupOpen(false);
                }}
                onSecondaryButtonClick={() => setIsConfirmationPopupOpen(false)}
                mainButtonText='Proceed'
            />
            <Popup
                isOpen={isApprovalConfirmationPopupOpen}
                headlineText={POPUP_DESCRIPTIONS.approval.headlineText}
                contentText={POPUP_DESCRIPTIONS.approval.contentText}
                onMainButtonClick={() => {
                    setIsApproval(true);
                    setIsApprovalPopupOpen(true);
                    setIsApprovalConfirmationPopupOpen(false);
                }}
                onSecondaryButtonClick={() => setIsApprovalConfirmationPopupOpen(false)}
                mainButtonText='Proceed'
            />

            <Popup
                isOpen={isChangesRequestedPopupOpen}
                headlineText={POPUP_DESCRIPTIONS.requestChange.headlineText}
                contentText={POPUP_DESCRIPTIONS.requestChange.contentText}
                onMainButtonClick={() => {
                    setIsApproval(false);
                    setIsApprovalPopupOpen(true);
                    setIsChangesRequestedPopupOpen(false);
                }}
                onSecondaryButtonClick={() => setIsChangesRequestedPopupOpen(false)}
                mainButtonText='Proceed'
            />
        </Box>
    );
};

export default ReviewApprovalSection;